<script setup lang="ts">
import { ToastService } from '@/general/services/toasts/toast.service';
import { TranslationService } from '@/general/services/translations/translation.service';
import { ScheduledPipelineProduct, ScheduledPipelinesRestService } from 'platform-unit2-api';
import { onMounted, ref } from 'vue';

/** Props */
export interface Props {
  pipelineId: number;
}

const props = defineProps<Props>();

/** Services */
const ts = new TranslationService('retailer', 'exports');
const scheduledPipelineApi = new ScheduledPipelinesRestService();

/** Constants */
const products = ref<ScheduledPipelineProduct[]>([]);
const loading = ref<boolean>(false);

const loadAsyncData = async () => {
  loading.value = true;
  try {
    products.value = await scheduledPipelineApi.getScheduledPipelineProducts(props.pipelineId);
  } catch {
    ToastService.getInstance().displayErrorToast('Failed to load products');
  } finally {
    loading.value = false;
  }
};

/** Lyfecycle */
onMounted(async () => {
  if (props.pipelineId) {
    await loadAsyncData();
  }
});
</script>

<template>
  <div class="p-2 w-full">
    <Message v-if="!loading && !products.length" severity="info" :closable="false">
      No products found
    </Message>

    <div v-else>
      <DataTable
        :value="products"
        :loading="loading"
        :paginator="true"
        :rows="5"
        paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        :rows-per-page-options="[5, 15, 30]"
        responsive-layout="scroll"
        current-page-report-template="Showing {first} to {last} of {totalRecords}"
      >
        <Column field="gtin" :header="ts.tModule('table_header.gtin')"></Column>
        <Column field="display_name" :header="ts.tModule('table_header.name')"></Column>
        <Column field="category_name" :header="ts.tModule('table_header.category')"></Column>
        <Column field="brand_name" :header="ts.tModule('table_header.brand')"></Column>
      </DataTable>
    </div>
    <ConfirmPopup group="product-list" />
  </div>
</template>
